// 🔍 Scan 탭 - 정합성 스캔 및 삭제 기능
// [배경] Non-canonical, Orphan, Duplicate 탐지 및 삭제

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  AlertTitle,
  LinearProgress,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  Checkbox,
  Collapse,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';

import {
  SSOTTestService,
  ScanResult,
  NonCanonicalModelItem,
  OrphanPriceItem,
  DuplicateVariantItem
} from '../../../../services/ssotTestService';

interface ScanTabProps {
  useTestCollection: boolean;
  onScanComplete: (result: ScanResult) => void;
  scanResult: ScanResult | null;
}

/**
 * 🔍 Scan 탭 컴포넌트
 * - Non-canonical modelId 탐지
 * - Orphan price 탐지
 * - Duplicate variant 탐지
 */
const ScanTab: React.FC<ScanTabProps> = ({
  useTestCollection,
  onScanComplete,
  scanResult
}) => {
  // === 상태 ===
  const [loading, setLoading] = useState(false);
  const [modelIdFilter, setModelIdFilter] = useState('');
  const [showTestOnly, setShowTestOnly] = useState(false);
  
  // 확장 상태
  const [expandNonCanonical, setExpandNonCanonical] = useState(true);
  const [expandOrphan, setExpandOrphan] = useState(true);
  const [expandDuplicate, setExpandDuplicate] = useState(true);
  
  // 🗑️ 삭제 관련 상태
  const [selectedNonCanonical, setSelectedNonCanonical] = useState<Set<string>>(new Set());
  const [selectedOrphan, setSelectedOrphan] = useState<Set<string>>(new Set());
  const [selectedDuplicate, setSelectedDuplicate] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<'nonCanonical' | 'orphan' | 'duplicate' | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{ success: boolean; message: string } | null>(null);
  
  // === 스캔 실행 ===
  const handleRunScan = useCallback(async () => {
    setLoading(true);
    try {
      const result = await SSOTTestService.runFullScan(
        showTestOnly || useTestCollection,
        modelIdFilter || undefined
      );
      onScanComplete(result);
    } catch (error) {
      console.error('스캔 실패:', error);
    }
    setLoading(false);
  }, [showTestOnly, useTestCollection, modelIdFilter, onScanComplete]);
  
  // === JSON 내보내기 ===
  const handleExportJson = useCallback(() => {
    if (!scanResult) return;
    
    const exportData = {
      scannedAt: scanResult.scannedAt,
      summary: {
        totalModels: scanResult.totalModels,
        totalPrices: scanResult.totalPrices,
        nonCanonicalCount: scanResult.nonCanonicalModels.length,
        orphanCount: scanResult.orphanPrices.length,
        duplicateCount: scanResult.duplicateVariants.length
      },
      nonCanonicalModels: scanResult.nonCanonicalModels,
      orphanPrices: scanResult.orphanPrices,
      duplicateVariants: scanResult.duplicateVariants
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ssot-scan-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [scanResult]);
  
  // === 복사 핸들러 ===
  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);
  
  // === 🗑️ 삭제 핸들러 ===
  
  // 비정규 모델 선택 토글
  const toggleNonCanonicalSelection = useCallback((id: string) => {
    setSelectedNonCanonical(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);
  
  // 고아 가격 선택 토글
  const toggleOrphanSelection = useCallback((priceId: string) => {
    setSelectedOrphan(prev => {
      const newSet = new Set(prev);
      if (newSet.has(priceId)) {
        newSet.delete(priceId);
      } else {
        newSet.add(priceId);
      }
      return newSet;
    });
  }, []);
  
  // 중복 구성 선택 토글 (키: modelId|storage|connectivity)
  const toggleDuplicateSelection = useCallback((key: string) => {
    setSelectedDuplicate(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);
  
  // 전체 선택/해제
  const selectAllNonCanonical = useCallback(() => {
    if (!scanResult) return;
    if (selectedNonCanonical.size === scanResult.nonCanonicalModels.length) {
      setSelectedNonCanonical(new Set());
    } else {
      setSelectedNonCanonical(new Set(scanResult.nonCanonicalModels.map(m => m.id)));
    }
  }, [scanResult, selectedNonCanonical.size]);
  
  const selectAllOrphan = useCallback(() => {
    if (!scanResult) return;
    if (selectedOrphan.size === scanResult.orphanPrices.length) {
      setSelectedOrphan(new Set());
    } else {
      setSelectedOrphan(new Set(scanResult.orphanPrices.map(p => p.priceId)));
    }
  }, [scanResult, selectedOrphan.size]);
  
  const selectAllDuplicate = useCallback(() => {
    if (!scanResult) return;
    if (selectedDuplicate.size === scanResult.duplicateVariants.length) {
      setSelectedDuplicate(new Set());
    } else {
      setSelectedDuplicate(new Set(scanResult.duplicateVariants.map(d => `${d.modelId}|${d.storage}|${d.connectivity}`)));
    }
  }, [scanResult, selectedDuplicate.size]);
  
  // 삭제 다이얼로그 열기
  const openDeleteDialog = useCallback((type: 'nonCanonical' | 'orphan' | 'duplicate') => {
    setDeleteType(type);
    setDeleteDialogOpen(true);
    setDeleteResult(null);
  }, []);
  
  // 삭제 실행
  const executeDelete = useCallback(async () => {
    if (!deleteType || !scanResult) return;
    
    setDeleting(true);
    setDeleteResult(null);
    
    try {
      let result: { success: boolean; message: string };
      
      switch (deleteType) {
        case 'nonCanonical': {
          const modelIds = Array.from(selectedNonCanonical);
          const deleteResult = await SSOTTestService.deleteNonCanonicalModels(modelIds, true);
          result = {
            success: deleteResult.errors.length === 0,
            message: `모델 ${deleteResult.deletedModels}개, 관련 가격 ${deleteResult.deletedPrices}개 삭제됨${deleteResult.errors.length > 0 ? `\n오류: ${deleteResult.errors.join(', ')}` : ''}`
          };
          setSelectedNonCanonical(new Set());
          break;
        }
        case 'orphan': {
          const priceIds = Array.from(selectedOrphan);
          const deleteResult = await SSOTTestService.deleteOrphanPrices(priceIds);
          result = {
            success: deleteResult.errors.length === 0,
            message: `가격 ${deleteResult.deleted}개 삭제됨${deleteResult.errors.length > 0 ? `\n오류: ${deleteResult.errors.join(', ')}` : ''}`
          };
          setSelectedOrphan(new Set());
          break;
        }
        case 'duplicate': {
          const duplicates = scanResult.duplicateVariants.filter(d => 
            selectedDuplicate.has(`${d.modelId}|${d.storage}|${d.connectivity}`)
          );
          const deleteResult = await SSOTTestService.deleteDuplicateVariants(duplicates);
          result = {
            success: deleteResult.errors.length === 0,
            message: `중복 가격 ${deleteResult.deleted}개 삭제됨 (${deleteResult.kept.length}개 유지 - 최신 데이터)${deleteResult.errors.length > 0 ? `\n오류: ${deleteResult.errors.join(', ')}` : ''}`
          };
          setSelectedDuplicate(new Set());
          break;
        }
        default:
          result = { success: false, message: '알 수 없는 삭제 유형' };
      }
      
      setDeleteResult(result);
      
      // 삭제 성공 시 자동 재스캔
      if (result.success) {
        setTimeout(() => {
          handleRunScan();
          setDeleteDialogOpen(false);
        }, 1500);
      }
      
    } catch (error) {
      setDeleteResult({
        success: false,
        message: `삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteType, scanResult, selectedNonCanonical, selectedOrphan, selectedDuplicate, handleRunScan]);
  
  // 삭제 대상 개수 계산
  const getDeleteCount = useCallback(() => {
    switch (deleteType) {
      case 'nonCanonical':
        return selectedNonCanonical.size;
      case 'orphan':
        return selectedOrphan.size;
      case 'duplicate':
        return selectedDuplicate.size;
      default:
        return 0;
    }
  }, [deleteType, selectedNonCanonical.size, selectedOrphan.size, selectedDuplicate.size]);
  
  // 삭제 유형 한글명
  const getDeleteTypeName = useCallback(() => {
    switch (deleteType) {
      case 'nonCanonical':
        return '비정규 모델';
      case 'orphan':
        return '고아 가격';
      case 'duplicate':
        return '중복 구성';
      default:
        return '';
    }
  }, [deleteType]);
  
  return (
    <Box>
      {/* 스캔 컨트롤 */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="특정 modelId로 필터링..."
            value={modelIdFilter}
            onChange={(e) => setModelIdFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={showTestOnly}
                onChange={(e) => setShowTestOnly(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                테스트 컬렉션만
              </Typography>
            }
          />
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Button
            variant="contained"
            startIcon={loading ? null : <RefreshIcon />}
            onClick={handleRunScan}
            disabled={loading}
          >
            {loading ? '스캔 중...' : '스캔 실행'}
          </Button>
          
          {scanResult && (
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExportJson}
            >
              JSON 내보내기
            </Button>
          )}
        </Box>
        
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Paper>
      
      {/* 스캔 결과 없음 */}
      {!scanResult && !loading && (
        <Alert severity="info">
          "스캔 실행" 버튼을 클릭하여 데이터 정합성을 검사하세요.
        </Alert>
      )}
      
      {/* 스캔 결과 */}
      {scanResult && (
        <>
          {/* 요약 카드 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight={800} color="primary">
                    {scanResult.totalModels}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    총 모델
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4" fontWeight={800} color="info.main">
                    {scanResult.totalPrices}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    총 가격
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card 
                variant="outlined"
                sx={{ 
                  bgcolor: scanResult.nonCanonicalModels.length > 0 ? 'warning.50' : 'success.50'
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography 
                    variant="h4" 
                    fontWeight={800} 
                    color={scanResult.nonCanonicalModels.length > 0 ? 'warning.main' : 'success.main'}
                  >
                    {scanResult.nonCanonicalModels.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    비정규 ID
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card 
                variant="outlined"
                sx={{ 
                  bgcolor: (scanResult.orphanPrices.length + scanResult.duplicateVariants.length) > 0 
                    ? 'error.50' : 'success.50'
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography 
                    variant="h4" 
                    fontWeight={800} 
                    color={(scanResult.orphanPrices.length + scanResult.duplicateVariants.length) > 0 
                      ? 'error.main' : 'success.main'}
                  >
                    {scanResult.orphanPrices.length + scanResult.duplicateVariants.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    고아 + 중복
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* 비정규 모델 ID */}
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                bgcolor: scanResult.nonCanonicalModels.length > 0 ? 'warning.50' : 'grey.50'
              }}
              onClick={() => setExpandNonCanonical(!expandNonCanonical)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color={scanResult.nonCanonicalModels.length > 0 ? 'warning' : 'disabled'} />
                <Typography fontWeight={700}>
                  비정규 모델 ID ({scanResult.nonCanonicalModels.length}개)
                </Typography>
                {selectedNonCanonical.size > 0 && (
                  <Chip 
                    label={`${selectedNonCanonical.size}개 선택됨`} 
                    size="small" 
                    color="primary" 
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {scanResult.nonCanonicalModels.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={selectedNonCanonical.size === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog('nonCanonical');
                    }}
                  >
                    선택 삭제
                  </Button>
                )}
                <IconButton size="small">
                  {expandNonCanonical ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
              </Box>
            </Box>
            
            <Collapse in={expandNonCanonical}>
              {scanResult.nonCanonicalModels.length === 0 ? (
                <Alert severity="success" sx={{ m: 2 }}>
                  모든 모델 ID가 정규 형식입니다. ✅
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedNonCanonical.size > 0 && selectedNonCanonical.size < scanResult.nonCanonicalModels.length}
                            checked={selectedNonCanonical.size === scanResult.nonCanonicalModels.length && scanResult.nonCanonicalModels.length > 0}
                            onChange={selectAllNonCanonical}
                          />
                        </TableCell>
                        <TableCell>현재 ID</TableCell>
                        <TableCell>권장 ID</TableCell>
                        <TableCell>문제점</TableCell>
                        <TableCell align="right">액션</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanResult.nonCanonicalModels.map((item, idx) => (
                        <TableRow 
                          key={idx} 
                          hover
                          selected={selectedNonCanonical.has(item.id)}
                          onClick={() => toggleNonCanonicalSelection(item.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedNonCanonical.has(item.id)}
                              onChange={() => toggleNonCanonicalSelection(item.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {item.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'success.main' }}
                            >
                              {item.suggestedCanonical}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {item.issues.map((issue, i) => (
                                <Chip key={i} label={issue} size="small" color="warning" variant="outlined" />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="권장 ID 복사">
                              <IconButton size="small" onClick={() => handleCopy(item.suggestedCanonical)}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Collapse>
          </Paper>
          
          {/* 고아 가격 */}
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                bgcolor: scanResult.orphanPrices.length > 0 ? 'error.50' : 'grey.50'
              }}
              onClick={() => setExpandOrphan(!expandOrphan)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color={scanResult.orphanPrices.length > 0 ? 'error' : 'disabled'} />
                <Typography fontWeight={700}>
                  고아 가격 ({scanResult.orphanPrices.length}개)
                </Typography>
                {selectedOrphan.size > 0 && (
                  <Chip 
                    label={`${selectedOrphan.size}개 선택됨`} 
                    size="small" 
                    color="primary" 
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {scanResult.orphanPrices.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={selectedOrphan.size === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog('orphan');
                    }}
                  >
                    선택 삭제
                  </Button>
                )}
                <IconButton size="small">
                  {expandOrphan ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
              </Box>
            </Box>
            
            <Collapse in={expandOrphan}>
              {scanResult.orphanPrices.length === 0 ? (
                <Alert severity="success" sx={{ m: 2 }}>
                  고아 가격이 없습니다. ✅
                </Alert>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedOrphan.size > 0 && selectedOrphan.size < scanResult.orphanPrices.length}
                            checked={selectedOrphan.size === scanResult.orphanPrices.length && scanResult.orphanPrices.length > 0}
                            onChange={selectAllOrphan}
                          />
                        </TableCell>
                        <TableCell>가격 ID</TableCell>
                        <TableCell>모델 ID</TableCell>
                        <TableCell>사유</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scanResult.orphanPrices.map((item, idx) => (
                        <TableRow 
                          key={idx} 
                          hover
                          selected={selectedOrphan.has(item.priceId)}
                          onClick={() => toggleOrphanSelection(item.priceId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedOrphan.has(item.priceId)}
                              onChange={() => toggleOrphanSelection(item.priceId)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {item.priceId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {item.modelId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={item.reason} size="small" color="error" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Collapse>
          </Paper>
          
          {/* 중복 구성 */}
          <Paper variant="outlined">
            <Box
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                bgcolor: scanResult.duplicateVariants.length > 0 ? 'error.50' : 'grey.50'
              }}
              onClick={() => setExpandDuplicate(!expandDuplicate)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color={scanResult.duplicateVariants.length > 0 ? 'error' : 'disabled'} />
                <Typography fontWeight={700}>
                  중복 구성 ({scanResult.duplicateVariants.length}개)
                </Typography>
                {selectedDuplicate.size > 0 && (
                  <Chip 
                    label={`${selectedDuplicate.size}개 선택됨`} 
                    size="small" 
                    color="primary" 
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {scanResult.duplicateVariants.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={selectedDuplicate.size === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog('duplicate');
                    }}
                  >
                    선택 삭제
                  </Button>
                )}
                <IconButton size="small">
                  {expandDuplicate ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
              </Box>
            </Box>
            
            <Collapse in={expandDuplicate}>
              {scanResult.duplicateVariants.length === 0 ? (
                <Alert severity="success" sx={{ m: 2 }}>
                  중복 구성이 없습니다. ✅
                </Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ m: 2, mb: 0 }}>
                    <AlertTitle>중복 삭제 시 동작</AlertTitle>
                    각 중복 그룹에서 <strong>최신 updatedAt</strong> 가격 문서를 유지하고, 나머지를 삭제합니다.
                    (✓: 유지, ✗: 삭제 대상)
                  </Alert>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedDuplicate.size > 0 && selectedDuplicate.size < scanResult.duplicateVariants.length}
                              checked={selectedDuplicate.size === scanResult.duplicateVariants.length && scanResult.duplicateVariants.length > 0}
                              onChange={selectAllDuplicate}
                            />
                          </TableCell>
                          <TableCell>모델 ID</TableCell>
                          <TableCell>저장소</TableCell>
                          <TableCell>연결 방식</TableCell>
                          <TableCell>중복 개수</TableCell>
                          <TableCell>중복 가격 ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scanResult.duplicateVariants.map((item, idx) => {
                          const key = `${item.modelId}|${item.storage}|${item.connectivity}`;
                          return (
                            <TableRow 
                              key={idx} 
                              hover
                              selected={selectedDuplicate.has(key)}
                              onClick={() => toggleDuplicateSelection(key)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedDuplicate.has(key)}
                                  onChange={() => toggleDuplicateSelection(key)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {item.modelId}
                                </Typography>
                              </TableCell>
                              <TableCell>{item.storage || '-'}</TableCell>
                              <TableCell>{item.connectivity || 'Wi-Fi'}</TableCell>
                              <TableCell>
                                <Chip label={`${item.count}개`} size="small" color="error" />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ maxWidth: 300, overflow: 'auto' }}>
                                  {item.priceIds.map((pid, i) => (
                                    <Typography 
                                      key={i} 
                                      variant="caption" 
                                      sx={{ 
                                        fontFamily: 'monospace', 
                                        display: 'block',
                                        // 첫 번째 ID는 유지됨을 표시
                                        color: i === 0 ? 'success.main' : 'text.secondary',
                                        fontWeight: i === 0 ? 700 : 400
                                      }}
                                    >
                                      {i === 0 ? '✓ ' : '✗ '}{pid}
                                    </Typography>
                                  ))}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Collapse>
          </Paper>
          
          {/* 스캔 시간 */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            스캔 시간: {scanResult.scannedAt.toLocaleString('ko-KR')}
          </Typography>
        </>
      )}
      
      {/* 🗑️ 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteIcon />
          {getDeleteTypeName()} 삭제 확인
        </DialogTitle>
        <DialogContent>
          {deleteResult ? (
            <Alert severity={deleteResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
              <AlertTitle>{deleteResult.success ? '삭제 완료' : '삭제 실패'}</AlertTitle>
              {deleteResult.message}
              {deleteResult.success && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  잠시 후 자동으로 재스캔합니다...
                </Typography>
              )}
            </Alert>
          ) : (
            <>
              <DialogContentText>
                선택한 <strong>{getDeleteCount()}개</strong>의 {getDeleteTypeName()}을(를) 삭제하시겠습니까?
              </DialogContentText>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>⚠️ 주의</AlertTitle>
                {deleteType === 'nonCanonical' && (
                  <>
                    • 비정규 모델 삭제 시 <strong>관련 가격 문서도 함께 삭제</strong>됩니다.<br />
                    • 이 작업은 되돌릴 수 없습니다.
                  </>
                )}
                {deleteType === 'orphan' && (
                  <>
                    • 고아 가격은 연결된 모델이 없는 가격 문서입니다.<br />
                    • 이 작업은 되돌릴 수 없습니다.
                  </>
                )}
                {deleteType === 'duplicate' && (
                  <>
                    • 각 중복 그룹에서 <strong>첫 번째 문서만 유지</strong>되고 나머지는 삭제됩니다.<br />
                    • 이 작업은 되돌릴 수 없습니다.
                  </>
                )}
              </Alert>
              
              {/* 삭제 대상 미리보기 */}
              <Paper variant="outlined" sx={{ mt: 2, p: 2, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2" gutterBottom>
                  삭제 대상 목록:
                </Typography>
                {deleteType === 'nonCanonical' && (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {Array.from(selectedNonCanonical).slice(0, 10).map((id, i) => (
                      <li key={i}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {id}
                        </Typography>
                      </li>
                    ))}
                    {selectedNonCanonical.size > 10 && (
                      <li>
                        <Typography variant="caption" color="text.secondary">
                          ... 외 {selectedNonCanonical.size - 10}개
                        </Typography>
                      </li>
                    )}
                  </Box>
                )}
                {deleteType === 'orphan' && (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {Array.from(selectedOrphan).slice(0, 10).map((id, i) => (
                      <li key={i}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {id}
                        </Typography>
                      </li>
                    ))}
                    {selectedOrphan.size > 10 && (
                      <li>
                        <Typography variant="caption" color="text.secondary">
                          ... 외 {selectedOrphan.size - 10}개
                        </Typography>
                      </li>
                    )}
                  </Box>
                )}
                {deleteType === 'duplicate' && (
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {Array.from(selectedDuplicate).slice(0, 10).map((key, i) => {
                      const [modelId, storage, conn] = key.split('|');
                      return (
                        <li key={i}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {modelId} / {storage || '-'} / {conn || 'Wi-Fi'}
                          </Typography>
                        </li>
                      );
                    })}
                    {selectedDuplicate.size > 10 && (
                      <li>
                        <Typography variant="caption" color="text.secondary">
                          ... 외 {selectedDuplicate.size - 10}개 그룹
                        </Typography>
                      </li>
                    )}
                  </Box>
                )}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
          >
            {deleteResult ? '닫기' : '취소'}
          </Button>
          {!deleteResult && (
            <Button
              onClick={executeDelete}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
              {deleting ? '삭제 중...' : `${getDeleteCount()}개 삭제`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScanTab;

